// Shared UI for the daily field report pages (PM approve + foreman edit).
// Both pages render the identical report structure — only the gating,
// banners, and the bottom action differ — so the layout, styles, and the
// editable section components live here once.
import React from 'react'
import { createPortal } from 'react-dom'
import { etDayOf } from '../lib/dateUtils'
import { searchScope, noteAfterPick, fmtManDays, foldText } from '../lib/scopeSearch'

// ── formatting helpers ────────────────────────────────────────────────────────
// Format a 'YYYY-MM-DD' work date for display. Built from the numeric parts via
// the LOCAL Date constructor (mirrors dateUtils.fmtLastWorked) — never parses the
// bare string through new Date('YYYY-MM-DD'), which would UTC-shift it a day.
export function fmtDate(dateStr) {
  if (!dateStr) return ''
  const parts = String(dateStr).split('-')
  if (parts.length !== 3) return dateStr
  const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
}
// True when the foreman submitted on an Eastern calendar day BEFORE the report's
// Work Date (e.g. filled this evening for tomorrow's job). Derived — both inputs
// already live on the report, so no stored column. Drives the "confirm hours"
// badge. Comparison is string-only ('YYYY-MM-DD' lexicographic).
export function isSubmittedEarly(report) {
  if (!report) return false
  const submittedDay = etDayOf(report.foreman_submitted_at)
  return !!submittedDay && !!report.report_date && submittedDay < report.report_date
}
export function fmtWhen(isoStr) {
  if (!isoStr) return ''
  return new Date(isoStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York', timeZoneName: 'short',
  })
}
export function fmtEditWindow(isoStr) {
  if (!isoStr) return ''
  const d = new Date(isoStr)
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York', timeZoneName: 'short' })
  const date = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'America/New_York' })
  return `${time} on ${date}`
}
export function fmtDuration(seconds) {
  if (!seconds) return null
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
}

// Immutable nested set: deepSet(obj, ['attendance', 0, 'hours_worked'], 8)
export function deepSet(obj, path, value) {
  if (path.length === 0) return value
  const [head, ...rest] = path
  const src = obj == null ? (typeof head === 'number' ? [] : {}) : obj
  const clone = Array.isArray(src) ? src.slice() : { ...src }
  clone[head] = deepSet(src[head], rest, value)
  return clone
}

// ── completeness rules ─────────────────────────────────────────────────────────
// MIRRORED SERVER COPY: api/lib/report-validation.js — keep the two identical.
// CRA's module scope forbids importing across the src/ ↔ api/ boundary.
export function isBracketName(n) {
  return typeof n === 'string' && n.trim().startsWith('[') && n.trim().endsWith(']')
}
// ── cost-code SPLITS (web-only) ───────────────────────────────────────────────
// A worker's day can be broken across several cost codes: an OPTIONAL
// `cost_splits: [{ cost_code, hours }]` on an attendance / unscheduled_workers
// row. Absent / null / empty  =>  exact legacy behavior (hours_worked +
// cost_code) — old reports and every phone-created row are untouched. With
// entries, the web UI keeps hours_worked synced to the SUM of the split hours
// and sets the row-level cost_code to NULL on purpose: any consumer that
// doesn't know about splits then shows the right TOTAL HOURS with a BLANK code
// (visible for Carla to chase) instead of silently coding the whole day to one
// of them. MIRRORED SERVER COPY: api/lib/report-validation.js.
export function activeSplits(row) {
  const s = row && row.cost_splits
  return Array.isArray(s) && s.length > 0 ? s : null
}
export function splitHoursTotal(splits) {
  const t = (Array.isArray(splits) ? splits : []).reduce((sum, s) => {
    const h = Number(s && s.hours)
    return sum + (Number.isFinite(h) ? h : 0)
  }, 0)
  return Math.round(t * 100) / 100
}
// One split line is offending when it has hours > 0 but no cost code.
export function splitNeedsCostCode(split) {
  if (!split) return false
  if (!(Number(split.hours) > 0)) return false
  const cc = split.cost_code
  return cc == null || String(cc).trim() === ''
}
// A row with hours > 0 must carry a cost code. 0/blank-hours rows are exempt.
// With active splits the rule applies PER SPLIT LINE (the row-level cost_code is
// deliberately null there) — offending when ANY split with hours > 0 has no code.
export function needsCostCode(row) {
  if (!row) return false
  const splits = activeSplits(row)
  if (splits) return splits.some(splitNeedsCostCode)
  const hours = Number(row.hours_worked)
  if (!(hours > 0)) return false
  const cc = row.cost_code
  return cc == null || String(cc).trim() === ''
}
// A union-placeholder slot is "unfilled" only when it still has NO real name —
// blank or a bracketed slot label. A real name from ANY source counts as filled.
export function isUnfilledPlaceholder(row) {
  if (!row || row.was_placeholder_slot !== true) return false
  const name = row.employee_name
  return name == null || String(name).trim() === '' || isBracketName(name)
}
// Display-only: is this attendance/manpower row a SCHEDULED worker (vs a walk-on)?
// Yes for seeded crew (publish stamps scheduled:true) and for any union slot
// (was_placeholder_slot) — planned whether or not filled, and a no-show is still
// scheduled. No for inbound-appended walk-ons and every unscheduled_workers[] row
// (neither field set). MIRRORED SERVER COPY: api/lib/report-validation.js.
export function isScheduledRow(row) {
  if (!row) return false
  return row.scheduled === true || row.was_placeholder_slot === true
}
// Display-time employee-id resolution (exact-only) — MIRRORS api/lib/worker-match.js
// displayEmployeeId. `empByLower` is a Map(name.toLowerCase() -> employee {id,name}).
// A stored employee_id (Phase 1 rows) or an EXACT name match → show the number;
// an unfilled bracket slot → nothing; a real name that doesn't resolve → flag
// "not in payroll yet". No fuzzy on the client (matches the server display policy).
export function displayEmployeeId(row, empByLower) {
  const r = row || {}
  const name = r.employee_name
  if (!name || isBracketName(name)) return { id: null, flagged: false }
  if (r.employee_id != null) return { id: r.employee_id, flagged: false }
  const hit = empByLower instanceof Map ? empByLower.get(String(name).trim().toLowerCase()) : null
  if (hit && hit.id != null) return { id: hit.id, flagged: false }
  return { id: null, flagged: true }
}
// Name -> employee map for the worker pickers. The roster now carries INACTIVE
// employees too (flagged active:false, 2026-10-19); on a shared name the ACTIVE
// one wins, so a name-only row never resolves to a former employee over a
// current one.
export function buildEmpByLower(roster) {
  const list = Array.isArray(roster) ? roster.filter(e => e && e.name) : []
  const m = new Map()
  for (const e of list) if (e.active === false) m.set(String(e.name).trim().toLowerCase(), e)
  for (const e of list) if (e.active !== false) m.set(String(e.name).trim().toLowerCase(), e)
  return m
}
// A worker who was ON SITE but carries 0 / blank hours. NOT a completeness
// failure and DELIBERATELY excluded from `ok` — it must never gate submit or
// approve, because a man can legitimately show up and leave. It exists purely to
// make the SILENT zero visible: on 2026-08-20 (report 483, job 592) a walk-on
// sat at 0 hours, passed submit AND approval unremarked, and simply never
// reached payroll. No-shows, rows dropped from the schedule and unfilled union
// slots are excluded — none of them is a missing-hours problem.
// MIRRORED SERVER COPY: api/lib/report-validation.js.
export function zeroHourShowed(row) {
  if (!row) return false
  if (row.showed_up === false) return false
  if (row._removed_from_schedule === true) return false
  const name = row.employee_name
  if (name == null || String(name).trim() === '' || isBracketName(name)) return false
  const splits = activeSplits(row)
  const hours = splits ? splitHoursTotal(splits) : Number(row.hours_worked)
  return !(Number(hours) > 0)
}
// Collect offending rows across attendance[] + unscheduled_workers[].
export function reportIssues(reportData) {
  const d = reportData || {}
  const lists = [
    ['attendance', Array.isArray(d.attendance) ? d.attendance : []],
    ['unscheduled_workers', Array.isArray(d.unscheduled_workers) ? d.unscheduled_workers : []],
  ]
  const missingCostCode = []
  const unfilledPlaceholders = []
  // ⚠ ADVISORY ONLY — see zeroHourShowed(). Never folded into `ok`.
  const zeroHours = []
  for (const [list, rows] of lists) {
    rows.forEach((row, index) => {
      if (needsCostCode(row)) missingCostCode.push({ list, index })
      if (isUnfilledPlaceholder(row)) unfilledPlaceholders.push({ list, index })
      if (zeroHourShowed(row)) zeroHours.push({ list, index, name: row.employee_name || '' })
    })
  }
  return { missingCostCode, unfilledPlaceholders, zeroHourShowed: zeroHours, ok: missingCostCode.length === 0 && unfilledPlaceholders.length === 0 }
}
// Shared user-facing copy for an incomplete submit/approve (single source).
export const INCOMPLETE_REPORT_MSG = 'Before submitting: add a cost code for every worker with hours, and assign a name to every open crew slot.'

// Error copy common to both pages. Pages spread this and add their own keys.
export const BASE_ERROR_COPY = {
  missing_token: 'This link is missing its security token. Please open the link directly from your email.',
  invalid_token: 'This link is invalid. Please use the most recent report email.',
  token_expired: 'This link has expired. Please ask for the report to be re-sent.',
  report_not_found: 'We could not find this report. It may have been removed.',
  report_deleted: 'This report has been removed. Please contact Carla Pereira.',
  server_misconfigured: 'The server is not configured correctly. Please contact Patrick.',
  db_error: 'Something went wrong. Please try again shortly.',
}

export const ISSUE_TYPES = [
  { key: 'delays', label: 'Delays' },
  { key: 'non_contract_work', label: 'Non-Contract Additional Work' },
  { key: 'safety_issues', label: 'Safety Issues' },
  { key: 'deliveries_without_receipt', label: 'Deliveries Without Receipt' },
  { key: 'equipment_issues', label: 'Equipment Breakdowns' },
]

// ── styles ────────────────────────────────────────────────────────────────────
export const sx = {
  page: { background: '#f4f4f4', minHeight: '100vh', padding: '24px 12px', fontFamily: 'Arial, Helvetica, sans-serif' },
  card: { maxWidth: 640, margin: '0 auto', background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,.12)' },
  header: { background: '#1e2d3d', padding: '24px 24px 20px', color: '#fff' },
  eyebrow: { fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: '#9ab', marginBottom: 6 },
  title: { fontSize: 20, fontWeight: 700, marginBottom: 10 },
  hMeta: { fontSize: 13, color: '#cdd8e3', lineHeight: 1.7 },
  body: { padding: '20px 24px' },
  h3: { fontSize: 14, fontWeight: 600, color: '#333', margin: '22px 0 8px', borderBottom: '2px solid #eee', paddingBottom: 6 },
  p: { fontSize: 13, color: '#444', lineHeight: 1.6, margin: '0 0 4px' },
  weather: { background: '#f0f7ff', borderLeft: '4px solid #4a90d9', padding: '10px 14px', margin: '16px 0', borderRadius: '0 6px 6px 0', fontSize: 13, color: '#333' },
  scrollX: { overflowX: 'auto', WebkitOverflowScrolling: 'touch', margin: '0 -4px' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 420 },
  th: { padding: '8px 8px', fontSize: 12, textAlign: 'left', borderBottom: '2px solid #ddd', color: '#555', whiteSpace: 'nowrap' },
  td: { padding: '6px 8px', fontSize: 13, borderBottom: '1px solid #eee', verticalAlign: 'middle' },
  input: { width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: 13, border: '1px solid #cfd6dd', borderRadius: 4, fontFamily: 'inherit' },
  num: { width: 70, boxSizing: 'border-box', padding: '6px 6px', fontSize: 13, border: '1px solid #cfd6dd', borderRadius: 4, textAlign: 'center' },
  textarea: { width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 13, border: '1px solid #cfd6dd', borderRadius: 4, fontFamily: 'inherit', lineHeight: 1.5, resize: 'vertical' },
  issueBox: { background: '#fff8f0', borderLeft: '4px solid #e8820c', padding: '10px 14px', margin: '10px 0', borderRadius: '0 6px 6px 0' },
  issueLabel: { fontSize: 13, color: '#b45309', fontWeight: 700 },
  muted: { fontSize: 12, color: '#888', margin: '12px 0' },
  empNum: { fontSize: 11, color: '#888', marginTop: 2 },
  noIdChip: { display: 'inline-block', marginTop: 3, fontSize: 11, color: '#b45309', background: '#fff8f0', border: '1px solid #f0d6b0', borderRadius: 4, padding: '0 6px' },
  inactiveWarn: { fontSize: 11, color: '#92510a', background: '#fff8f0', border: '1px solid #f0d6b0', borderRadius: 4, padding: '3px 6px', marginTop: 4, lineHeight: 1.4 },
  earlyChip: { display: 'inline-block', marginTop: 10, fontSize: 12, color: '#92510a', background: '#fff8f0', border: '1px solid #f0d6b0', borderRadius: 4, padding: '2px 8px' },
  banner: (bg, bd, fg) => ({ background: bg, borderLeft: `4px solid ${bd}`, color: fg, padding: '12px 16px', borderRadius: '0 6px 6px 0', fontSize: 13, lineHeight: 1.5, margin: '0 0 16px' }),
  footer: { padding: '4px 24px 24px' },
  actionBtn: (disabled, color = '#1e7e34', disabledColor = '#9bbfa6') => ({
    display: 'block', width: '100%', boxSizing: 'border-box', padding: '14px 0',
    fontSize: 16, fontWeight: 700, color: '#fff', border: 'none', borderRadius: 6,
    background: disabled ? disabledColor : color, cursor: disabled ? 'not-allowed' : 'pointer',
  }),
  brand: { background: '#f4f4f4', padding: '12px 24px', fontSize: 11, color: '#999', textAlign: 'center', borderTop: '1px solid #eee' },
  center: { textAlign: 'center', padding: '40px 24px' },
}

export function Shell({ children }) {
  return <div style={sx.page}><div style={sx.card}>{children}</div></div>
}

// `action` is an optional slot rendered under the message (a way back into the
// app, or a "you can close this window" line). Callers that pass nothing render
// exactly as before — a Notice with no action is a dead end on standalone pages.
export function Notice({ icon, title, msg, action }) {
  return (
    <Shell>
      <div style={sx.center}>
        <div style={{ fontSize: 34, marginBottom: 10 }}>{icon}</div>
        <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 6, color: '#1e2d3d' }}>{title}</div>
        <div style={{ fontSize: 13, color: '#666', lineHeight: 1.6 }}>{msg}</div>
        {!!action && <div style={{ marginTop: 18 }}>{action}</div>}
      </div>
    </Shell>
  )
}

// ── normalization ─────────────────────────────────────────────────────────────
export function normalizeReportData(d) {
  const x = d || {}
  return {
    work_description: x.work_description ?? '',
    attendance: Array.isArray(x.attendance) ? x.attendance : [],
    unscheduled_workers: Array.isArray(x.unscheduled_workers) ? x.unscheduled_workers : [],
    delays: x.delays || { occurred: false, description: null },
    non_contract_work: x.non_contract_work || { occurred: false, description: null },
    safety_issues: x.safety_issues || { occurred: false, description: null },
    deliveries_without_receipt: x.deliveries_without_receipt || { occurred: false, description: null },
    equipment_issues: x.equipment_issues || { occurred: false, description: null },
    production: {
      concrete: Array.isArray(x.production?.concrete) ? x.production.concrete : [],
      masonry: Array.isArray(x.production?.masonry) ? x.production.masonry : [],
    },
    tomorrow_needs: x.tomorrow_needs ?? '',
  }
}

// ── field components ───────────────────────────────────────────────────────────
function ScrollX({ children }) {
  return <div style={sx.scrollX}>{children}</div>
}

export function WeatherBlock({ report }) {
  const parts = []
  if (report.weather_temp_high_f != null && report.weather_temp_low_f != null) parts.push(`${report.weather_temp_high_f}°F / ${report.weather_temp_low_f}°F`)
  if (report.weather_conditions) parts.push(report.weather_conditions)
  if (report.weather_precipitation_in != null) parts.push(`precip ${report.weather_precipitation_in}"`)
  if (report.weather_humidity_pct != null) parts.push(`humidity ${report.weather_humidity_pct}%`)
  if (report.weather_wind_mph != null) parts.push(`wind ${report.weather_wind_mph} mph`)
  if (parts.length === 0) return null
  return <div style={sx.weather}>☁️ <strong>Weather:</strong> {parts.join(' · ')}</div>
}

function NumCell({ ro, value, onChange, required }) {
  if (ro) return <span>{value === '' || value == null ? '—' : value}</span>
  const empty = value == null || value === ''
  const style = required && empty
    ? { ...sx.num, border: '1px solid #d9534f', background: '#fff7f7' }
    : sx.num
  return (
    <input
      type="number" inputMode="decimal" style={style} value={value == null ? '' : value}
      onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
      min="0" step="0.5"
    />
  )
}
function TextCell({ ro, value, onChange, placeholder }) {
  if (ro) return <span>{value || '—'}</span>
  return <input type="text" style={sx.input} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
}

// Cost code picker. Falls back to a free-text input when the job has no
// cost codes available. Shows the description under the dropdown so the
// editor can confirm the pick; preserves any off-list value already present.
const REQUIRED_FIELD_STYLE = { border: '1px solid #d9534f', background: '#fff7f7' }

// PlanSwift "task -> cost code" picker (job_scope_items). Full-screen on a phone
// (foremen use the emailed token page on their phones), a centered sheet on a
// desktop. Tapping a task hands the whole scope row to `onPickTask`; tapping an
// entry under "Other cost codes" only sets the code; Clear sets it to null.
function fmtQty(q) {
  const n = Number(q)
  if (!Number.isFinite(n) || n === 0) return ''
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 })
}
const pickerBtn = { border: '1px solid #cfd6dd', background: '#f7f9fb', color: '#1e2d3d', borderRadius: 4, padding: '8px 14px', fontSize: 14, cursor: 'pointer', fontWeight: 600 }

function ScopePicker({ costCodes, scopeItems, value, onPickTask, onPickCode, onClear, onClose }) {
  const [q, setQ] = React.useState('')
  const groups = React.useMemo(() => searchScope(scopeItems, costCodes, q), [scopeItems, costCodes, q])
  const tokens = foldText(q).split(' ').filter(Boolean)
  const otherCodes = (Array.isArray(costCodes) ? costCodes : [])
    .filter(c => tokens.every(t => foldText(`${c.code} ${c.description || ''}`).includes(t)))
  const narrow = typeof window !== 'undefined' && window.innerWidth < 700
  React.useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  const cur = value == null ? '' : String(value)

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000, display: 'flex', alignItems: narrow ? 'stretch' : 'center', justifyContent: 'center', fontFamily: 'Arial, Helvetica, sans-serif' }
  const panel = { background: '#fff', width: narrow ? '100%' : 'min(680px, 94vw)', height: narrow ? '100%' : 'auto', maxHeight: narrow ? '100%' : '88vh', display: 'flex', flexDirection: 'column', borderRadius: narrow ? 0 : 8, boxShadow: '0 6px 24px rgba(0,0,0,.25)', overflow: 'hidden' }
  const groupHead = code => ({ position: 'sticky', top: 0, background: code === cur ? '#e6f4ea' : '#eef2f7', color: '#1e2d3d', fontWeight: 700, fontSize: 13, padding: '8px 14px', borderBottom: '1px solid #dde3ea', zIndex: 1 })
  const rowBtn = { display: 'block', width: '100%', textAlign: 'left', border: 'none', borderBottom: '1px solid #eef0f3', background: '#fff', padding: '10px 14px', minHeight: 48, cursor: 'pointer', fontFamily: 'inherit' }

  const body = (
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={e => e.stopPropagation()} role="dialog" aria-label="Pick task or cost code">
        <div style={{ padding: '12px 14px', borderBottom: '1px solid #e3e7ec', background: '#1e2d3d', color: '#fff' }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>What task did they work on?</div>
          <input type="search" autoFocus value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search task — e.g. stairs, strip forms, rebar"
            style={{ ...sx.input, fontSize: 16, padding: '10px 12px' }} />
        </div>
        <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {groups.length === 0 && (
            <div style={{ padding: '14px', fontSize: 13, color: '#888' }}>No estimate tasks match “{q}”. Pick a cost code below.</div>
          )}
          {groups.map(g => (
            <div key={g.code}>
              <div style={groupHead(g.code)}>{g.code}{g.description ? ` — ${g.description}` : ''}{g.code === cur ? '  ✓' : ''}</div>
              {g.items.map(({ item, matchedPhrase }, k) => {
                const meta = [fmtQty(item.qty) && `${fmtQty(item.qty)} ${item.unit || ''}`.trim(), fmtManDays(item.man_days)].filter(Boolean)
                return (
                  <button key={k} type="button" style={rowBtn} onClick={() => onPickTask(item)}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1e2d3d' }}>
                      {item.task_name}
                      {item.area && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, background: '#4a90d9', color: '#fff', borderRadius: 3, padding: '0 5px' }}>{item.area}</span>}
                    </div>
                    {item.size_text && <div style={{ fontSize: 12, color: '#6b7684', marginTop: 2 }}>{item.size_text}</div>}
                    {meta.length > 0 && <div style={{ fontSize: 12, color: '#444', marginTop: 2 }}>{meta.join(' · ')}</div>}
                    {matchedPhrase && <div style={{ fontSize: 11, color: '#1e7e34', marginTop: 2 }}>matches: {matchedPhrase}</div>}
                  </button>
                )
              })}
            </div>
          ))}
          {otherCodes.length > 0 && (
            <div>
              <div style={{ ...groupHead(''), background: '#f4f4f4', color: '#555' }}>Other cost codes (sets the code only)</div>
              {otherCodes.map(c => (
                <button key={c.code} type="button" style={{ ...rowBtn, minHeight: 44, background: c.code === cur ? '#e6f4ea' : '#fff' }} onClick={() => onPickCode(c.code)}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#1e2d3d' }}>{c.code}</span>
                  {c.description && <span style={{ fontSize: 13, color: '#555' }}> — {c.description}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '10px 14px', borderTop: '1px solid #e3e7ec', background: '#fafbfc' }}>
          {cur && <button type="button" style={{ ...pickerBtn, color: '#c0392b', marginRight: 'auto' }} onClick={onClear}>Clear code</button>}
          <button type="button" style={pickerBtn} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  )
  return typeof document !== 'undefined' ? createPortal(body, document.body) : body
}

// `scopeItems` / `onPickTask` (optional): when the job has PlanSwift scope rows and
// the cell is editable, the <select> becomes a button that opens ScopePicker.
// `onPickTask(item)` owns the WHOLE update for a task pick — the cost code AND the
// note — because the table rows patch from the row captured at render, so two
// separate patches (onChange then a note write) would clobber each other. Without
// scope rows the plain <select> renders exactly as before.
function CostCodeCell({ ro, value, onChange, costCodes, invalid, compact, scopeItems, onPickTask }) {
  const [pickerOpen, setPickerOpen] = React.useState(false)
  const hasCodes = Array.isArray(costCodes) && costCodes.length > 0
  const codeStr = value == null || value === '' ? '' : String(value)

  if (!hasCodes) {
    if (ro) return <span>{codeStr || '—'}</span>
    return (
      <div>
        <input type="text" style={invalid ? { ...sx.input, ...REQUIRED_FIELD_STYLE } : sx.input} value={codeStr} onChange={e => onChange(e.target.value)} placeholder="code" />
        {invalid && <div style={{ fontSize: 11, color: '#c0392b', marginTop: 2 }}>Cost code required</div>}
      </div>
    )
  }

  const known = costCodes.find(c => c.code === codeStr)
  const label = c => c.code + (c.description ? ` — ${c.description}` : '')

  if (ro) {
    if (!codeStr) return <span>—</span>
    return <span>{known ? label(known) : codeStr}</span>
  }

  const hasScope = Array.isArray(scopeItems) && scopeItems.length > 0
  if (hasScope) {
    const close = () => setPickerOpen(false)
    return (
      <div>
        <button type="button" onClick={() => setPickerOpen(true)} title="Search the estimate by task"
          style={{ ...sx.input, textAlign: 'left', cursor: 'pointer', background: '#fff', minHeight: 34, ...(invalid && !codeStr ? REQUIRED_FIELD_STYLE : {}) }}>
          {codeStr
            ? (known ? label(known) : `${codeStr} — (not in this job’s list)`)
            : <span style={{ color: '#4a90d9', fontWeight: 600 }}>Pick task / cost code</span>}
          <span style={{ float: 'right', color: '#888' }}>🔍</span>
        </button>
        {codeStr && !known && (
          <div style={{ fontSize: 11, color: '#c0392b', marginTop: 2 }}>Code not in this job’s cost code list</div>
        )}
        {invalid && !codeStr && (
          <div style={{ fontSize: 11, color: '#c0392b', marginTop: 2 }}>Cost code required</div>
        )}
        {pickerOpen && (
          <ScopePicker costCodes={costCodes} scopeItems={scopeItems} value={codeStr} onClose={close}
            onPickTask={item => { close(); if (typeof onPickTask === 'function') onPickTask(item); else onChange(item.cost_code) }}
            onPickCode={code => { close(); onChange(code) }}
            onClear={() => { close(); onChange(null) }} />
        )}
      </div>
    )
  }

  return (
    <div>
      <select
        style={invalid ? { ...sx.input, padding: '6px 6px', ...REQUIRED_FIELD_STYLE } : { ...sx.input, padding: '6px 6px' }}
        value={codeStr}
        onChange={e => onChange(e.target.value === '' ? null : e.target.value)}
      >
        <option value="">— select —</option>
        {codeStr && !known && <option value={codeStr}>{codeStr} — (not in this job’s list)</option>}
        {costCodes.map(c => <option key={c.code} value={c.code}>{label(c)}</option>)}
      </select>
      {known && known.description && !compact && (
        <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{known.description}</div>
      )}
      {codeStr && !known && (
        <div style={{ fontSize: 11, color: '#c0392b', marginTop: 2 }}>Code not in this job’s cost code list</div>
      )}
      {invalid && !codeStr && (
        <div style={{ fontSize: 11, color: '#c0392b', marginTop: 2 }}>Cost code required</div>
      )}
    </div>
  )
}

// ── cost-code split cells ─────────────────────────────────────────────────────
const linkBtnStyle = { marginTop: 3, border: 'none', background: 'none', color: '#4a90d9', cursor: 'pointer', fontSize: 11, padding: 0 }
const splitLineStyle = { display: 'flex', alignItems: 'flex-start', gap: 4, minHeight: 34, marginBottom: 4 }
const blankSplit = () => ({ cost_code: null, hours: null })

// Row patch for a new split list. Keeps hours_worked synced to the SUM and
// nulls the row-level cost_code while splits are active. Collapsing back to a
// SINGLE line reverts the row to plain single-code mode — that line's hours and
// code move back onto hours_worked / cost_code and cost_splits is cleared — so a
// row never ends up in a half-split state.
function splitPatch(row, splits) {
  const list = Array.isArray(splits) ? splits : []
  if (list.length <= 1) {
    const only = list[0]
    return {
      cost_splits: null,
      hours_worked: only ? (only.hours ?? null) : (row.hours_worked ?? null),
      cost_code: only ? (only.cost_code ?? null) : (row.cost_code ?? null),
    }
  }
  return { cost_splits: list, cost_code: null, hours_worked: splitHoursTotal(list) }
}

// The Hours + Cost Code cell bodies for one manpower / walk-on row, in either
// plain (one code) or SPLIT mode. Returned as a pair so each table drops them
// into its own <td>s. `setRow` takes a whole-row patch (the existing setRow /
// setRowA pattern), which is what keeps hours_worked = sum on every split edit.
// WEB-ONLY: the phone flow still captures one code per worker, and a phoned-in
// hours/cost-code update clears any splits (applyExtractionToRow in
// api/payroll/call-results.js).
function costCodeCells({ row, ro, costCodes, invalid, requiredHours, setRow, scope }) {
  const splits = activeSplits(row)
  // Task picks write the task name into this row's task_note (noteAfterPick).
  const scopeItems = scope ? scope.items : null
  const pickedNote = item => noteAfterPick(row.task_note, item, scope ? scope.names : [])

  if (!splits) {
    return {
      hours: <NumCell ro={ro} value={row.hours_worked} onChange={v => setRow({ hours_worked: v })} required={requiredHours} />,
      code: (
        <>
          <CostCodeCell ro={ro} value={row.cost_code} onChange={v => setRow({ cost_code: v })} costCodes={costCodes} invalid={invalid}
            scopeItems={scopeItems} onPickTask={item => setRow({ cost_code: item.cost_code, task_note: pickedNote(item) })} />
          {!ro && (
            <button type="button" style={linkBtnStyle} title="Break this worker's day across more than one cost code"
              onClick={() => setRow(splitPatch(row, [{ cost_code: row.cost_code ?? null, hours: row.hours_worked ?? null }, blankSplit()]))}>
              + split cost codes
            </button>
          )}
        </>
      ),
    }
  }

  const total = splitHoursTotal(splits)
  if (ro) {
    return {
      hours: <strong>{total}</strong>,
      code: (
        <div>
          {splits.map((s, k) => (
            <div key={k} style={{ fontSize: 13 }}>{s.cost_code || '—'} — {Number(s.hours) || 0}h</div>
          ))}
        </div>
      ),
    }
  }

  const setSplits = (list) => setRow(splitPatch(row, list))
  return {
    hours: (
      <div>
        {splits.map((s, k) => (
          <div key={k} style={{ ...splitLineStyle, justifyContent: 'center' }}>
            <NumCell ro={false} value={s.hours} onChange={v => setSplits(splits.map((x, j) => (j === k ? { ...x, hours: v } : x)))} />
          </div>
        ))}
        <div style={{ fontSize: 13, fontWeight: 700, color: requiredHours && !(total > 0) ? '#c0392b' : '#333' }}>Total: {total}</div>
      </div>
    ),
    code: (
      <div>
        {splits.map((s, k) => (
          <div key={k} style={splitLineStyle}>
            <div style={{ flex: 1 }}>
              <CostCodeCell ro={false} value={s.cost_code} costCodes={costCodes} compact
                invalid={invalid && splitNeedsCostCode(s)}
                onChange={v => setSplits(splits.map((x, j) => (j === k ? { ...x, cost_code: v } : x)))}
                scopeItems={scopeItems}
                onPickTask={item => setRow({
                  ...splitPatch(row, splits.map((x, j) => (j === k ? { ...x, cost_code: item.cost_code } : x))),
                  task_note: pickedNote(item),
                })} />
            </div>
            <button type="button" onClick={() => setSplits(splits.filter((_, j) => j !== k))} title="Remove this cost code line"
              style={{ border: 'none', background: 'none', color: '#c0392b', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '4px 2px' }}>×</button>
          </div>
        ))}
        <button type="button" style={linkBtnStyle} onClick={() => setSplits([...splits, blankSplit()])}>+ Add code</button>
      </div>
    ),
  }
}

// Payroll-id indicator under a worker name: the P4C number when linked, or an
// amber "not in payroll yet" action prompt when a named row has no resolvable id.
function EmpIdTag({ row, empByLower, empById }) {
  const { id, flagged } = displayEmployeeId(row, empByLower)
  if (flagged) return <div style={sx.noIdChip} title="No matching payroll employee — add them in Foundation, then re-link from the roster.">⚠ not in payroll yet</div>
  if (id != null) {
    const emp = empById instanceof Map ? empById.get(String(id)) : null
    if (emp && emp.active === false) return <div style={sx.noIdChip} title="This employee is marked inactive. Fine for someone coming back to work — let Carla know they're back.">#{id} · inactive</div>
    return <div style={sx.empNum} title="Payroll employee number (Foundation / P4C)">#{id}</div>
  }
  return null
}

// Amber advisory under a manpower / walk-on table: named workers marked on site
// with no hours. NON-BLOCKING BY DESIGN — zeroHourShowed() is deliberately kept
// out of reportIssues().ok, so this never disables Submit or Approve. It exists
// because a silent 0 is invisible: the row renders, the report validates, and
// the day simply never reaches payroll (report 483, 2026-08-20). Edit surfaces
// only — a read-only render already shows the zero plainly.
function ZeroHoursNote({ rows, ro }) {
  if (ro) return null
  const names = (Array.isArray(rows) ? rows : [])
    .filter(zeroHourShowed)
    .map(r => (r.employee_name || '').trim())
    .filter(Boolean)
  if (!names.length) return null
  return (
    <div style={sx.issueBox}>
      <span style={sx.issueLabel}>&#9888; No hours entered for {names.join(', ')}</span>
      <div style={{ fontSize: 12, color: '#8a5a1a', marginTop: 4, lineHeight: 1.5 }}>
        They&#8217;re marked on site with 0 hours, so they will not appear on the payroll CSV.
        Enter their hours, or untick &#8220;On site&#8221; if they didn&#8217;t work. You can still submit either way.
      </div>
    </div>
  )
}

function ManpowerTable({ rows, ro, set, costCodes, fillIn, onDeleteRow, employees, takenNames, validate, scope }) {
  if (!rows || rows.length === 0) return <p style={sx.muted}>No manpower recorded.</p>
  const showDelete = !ro && typeof onDeleteRow === 'function'
  // Roster lookup for resolving union-placeholder slots into named workers.
  const roster = Array.isArray(employees) ? employees.filter(e => e && e.name) : []
  const empByLower = buildEmpByLower(roster)
  const empById = new Map(roster.map(e => [String(e.id), e]))
  const taken = takenNames instanceof Set ? takenNames : new Set()
  const setRowA = (i, patch) => set(['attendance', i], { ...rows[i], ...patch })
  return (
    <>
    <ScrollX>
      <table style={sx.table}>
        <thead><tr>
          <th style={sx.th}>Employee</th>
          <th style={{ ...sx.th, textAlign: 'center' }}>On site</th>
          <th style={{ ...sx.th, textAlign: 'center' }}>Scheduled?</th>
          <th style={{ ...sx.th, textAlign: 'center' }}>Actual Hours</th>
          <th style={sx.th}>Cost Code</th>
          <th style={sx.th}>Notes</th>
          {showDelete && <th style={{ ...sx.th, textAlign: 'center' }}></th>}
        </tr></thead>
        <tbody>
          {rows.map((a, i) => {
            const removed = !!a._removed_from_schedule
            let name
            // Editable union-placeholder slot → resolve it in place via the roster
            // dropdown. Picking a name fills employee_name + employee_id and clears
            // the placeholder marker (cost code + hours on the row are preserved).
            if (!ro && a.was_placeholder_slot && !removed) {
              name = <WalkOnNameCell ro={false} row={a} idx={i} setRow={setRowA} empByLower={empByLower} empById={empById} roster={roster} taken={taken} pickExtra={{ was_placeholder_slot: false }} invalid={validate && isUnfilledPlaceholder(a)} />
            }
            else if (removed) name = <span style={{ textDecoration: 'line-through', color: '#999' }}>{a.employee_name || '—'}</span>
            else if (a.was_placeholder_slot && a.showed_up && a.employee_name) name = <>{a.employee_name} <span style={{ color: '#888', fontSize: 11 }}>(filled trade slot)</span></>
            else if (a.was_placeholder_slot && !a.showed_up) name = <em style={{ color: '#999' }}>[Trade slot — unfilled]</em>
            else name = a.employee_name || '—'
            const rowBg = removed ? '#f3f3f3' : (i % 2 ? '#f9f9f9' : '#fff')
            // Required-hours indicator only for live (not removed) scheduled rows.
            const reqHours = fillIn && !removed && a.showed_up
            const cells = costCodeCells({
              row: a, ro, costCodes, invalid: validate && needsCostCode(a),
              requiredHours: reqHours, setRow: patch => setRowA(i, patch), scope,
            })
            return (
              <tr key={i} style={{ background: rowBg }}>
                <td style={sx.td}>
                  {name}
                  {removed && <div style={{ fontSize: 10, color: '#b45309', marginTop: 2 }}>Removed from schedule</div>}
                  <EmpIdTag row={a} empByLower={empByLower} empById={empById} />
                </td>
                <td style={{ ...sx.td, textAlign: 'center' }}>
                  {ro ? (a.showed_up ? '✓' : '—')
                    : <input type="checkbox" checked={!!a.showed_up} onChange={e => set(['attendance', i, 'showed_up'], e.target.checked)} />}
                </td>
                <td style={{ ...sx.td, textAlign: 'center', color: isScheduledRow(a) ? '#1e7e34' : '#b45309' }}>{isScheduledRow(a) ? 'Yes' : 'No'}</td>
                <td style={{ ...sx.td, textAlign: 'center' }}>{cells.hours}</td>
                <td style={sx.td}>{cells.code}</td>
                <td style={sx.td}><TextCell ro={ro} value={a.task_note} onChange={v => set(['attendance', i, 'task_note'], v)} /></td>
                {showDelete && (
                  <td style={{ ...sx.td, textAlign: 'center' }}>
                    <button type="button" onClick={() => onDeleteRow(i)} title="Remove this worker"
                      style={{ border: 'none', background: 'none', color: '#c0392b', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </ScrollX>
    <ZeroHoursNote rows={rows} ro={ro} />
    </>
  )
}

const blankUnscheduledWorker = () => ({ employee_name: '', employee_id: null, trade: null, hours_worked: null, cost_code: null, task_note: null, _manual: false })

const INACTIVE_WARN = 'This employee is marked inactive. That’s fine for someone coming back to work — let Carla know they’re back.'

// Searchable employee picker (2026-10-19) — replaces the plain <select> on the
// report worker rows: a search box over the roster (name or #id, accent- and
// case-insensitive, every word must match), active employees listed first,
// inactive ones labelled. Rendered IN FLOW, not absolutely positioned, because
// the tables sit in a horizontal ScrollX that would clip a floating list.
// Picks by employee OBJECT (id), never by name, so two people who share a name
// can't be confused. variant 'field' = the dropdown box; 'link' = a small text
// link that opens the same list (used under the free-text new-hire field).
function EmployeePicker({ variant = 'field', label, selected, opts, onPick, onClear, onManual, invalid }) {
  const [open, setOpen] = React.useState(false)
  const [q, setQ] = React.useState('')
  const boxRef = React.useRef(null)
  React.useEffect(() => {
    if (!open) return undefined
    const away = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) { setOpen(false); setQ('') } }
    document.addEventListener('mousedown', away)
    document.addEventListener('touchstart', away)
    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('touchstart', away)
    }
  }, [open])
  const close = () => { setOpen(false); setQ('') }
  const choose = (emp) => { close(); onPick(emp) }
  const tokens = foldText(q).split(' ').filter(Boolean)
  const matches = (opts || [])
    .filter(e => tokens.every(t => foldText(`${e.name} ${e.id}`).includes(t)))
    .sort((a, b) => (a.active === false ? 1 : 0) - (b.active === false ? 1 : 0))   // stable: active first, then by name
  const LIMIT = 300
  const shown = matches.slice(0, LIMIT)
  const itemStyle = (on) => ({
    display: 'block', width: '100%', textAlign: 'left', padding: '9px 10px', fontSize: 13, border: 'none',
    borderBottom: '1px solid #f0f2f4', background: on ? '#eef4fb' : '#fff', cursor: 'pointer', fontFamily: 'inherit', color: '#1f2430',
  })
  const trigger = variant === 'link' ? (
    <button type="button" onClick={() => setOpen(o => !o)}
      style={{ marginTop: 2, border: 'none', background: 'none', color: '#4a90d9', cursor: 'pointer', fontSize: 11, padding: 0 }}>
      {label || 'choose from list'}
    </button>
  ) : (
    <button type="button" onClick={() => setOpen(o => !o)}
      style={{ ...sx.input, ...(invalid ? REQUIRED_FIELD_STYLE : {}), background: invalid ? REQUIRED_FIELD_STYLE.background : '#fff', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: selected ? '#1f2430' : '#888' }}>
        {selected ? `${selected.name}${selected.active === false ? ' (inactive)' : ''}` : '— select —'}
      </span>
      <span style={{ color: '#888', fontSize: 11, flexShrink: 0 }}>▾</span>
    </button>
  )
  return (
    <div ref={boxRef} style={{ minWidth: 190 }}>
      {trigger}
      {open && (
        <div style={{ marginTop: 4, border: '1px solid #cfd6dd', borderRadius: 6, background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,.08)', overflow: 'hidden' }}>
          <input type="text" autoFocus value={q} placeholder="Search name or #…"
            style={{ ...sx.input, border: 'none', borderBottom: '1px solid #e2e6ea', borderRadius: 0, padding: '8px 10px' }}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Escape') { e.preventDefault(); close() }
              else if (e.key === 'Enter') { e.preventDefault(); if (shown.length) choose(shown[0]) }
            }} />
          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            {selected && onClear && (
              <button type="button" style={{ ...itemStyle(false), color: '#888' }} onClick={() => { close(); onClear() }}>— clear selection —</button>
            )}
            {shown.map(e => (
              <button key={e.id} type="button" style={itemStyle(selected && String(selected.id) === String(e.id))} onClick={() => choose(e)}>
                {e.name} <span style={{ color: '#999', fontSize: 11 }}>#{e.id}</span>
                {e.active === false && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, color: '#b45309' }}>inactive</span>}
              </button>
            ))}
            {matches.length > shown.length && (
              <div style={{ padding: '6px 10px', fontSize: 11, color: '#888' }}>{matches.length - shown.length} more — keep typing to narrow it down</div>
            )}
            {tokens.length > 0 && matches.length === 0 && (
              <div style={{ padding: '8px 10px', fontSize: 12, color: '#888' }}>No employee matches “{q}”.</div>
            )}
            {onManual && (
              <button type="button" style={{ ...itemStyle(false), color: '#4a90d9', borderBottom: 'none' }} onClick={() => { close(); onManual() }}>+ Add someone not listed…</button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// Name cell for a worker row: a searchable roster picker (EmployeePicker) minus
// anyone already on the report, with an explicit "+ Add someone not listed…"
// escape to a free-text field for new hires. Picking stores the exact name AND
// the employee id so the payroll CSV resolves a real Employee ID. Used for
// walk-on rows AND for resolving union-placeholder attendance slots in place —
// the caller passes `pickExtra` (e.g. { was_placeholder_slot: false }) merged
// into the patch when a roster name is chosen.
// The roster carries INACTIVE employees for every viewer, foreman included
// (2026-10-19): a worker coming back for a day can be picked by name — marked
// "inactive" in the list, with a warning under the pick, never a block. A
// foreman who still types a name in by hand can be linked by the PM here.
function WalkOnNameCell({ ro, row, idx, setRow, empByLower, empById, roster, taken, pickExtra = {}, invalid }) {
  const rawName = row.employee_name || ''
  // An unfilled union placeholder still carries its bracketed slot text as the
  // name — treat that as "no pick yet" so the row opens on the picker.
  const effectiveName = isBracketName(rawName) ? '' : rawName
  const ownLower = effectiveName.trim().toLowerCase()
  // The stored id first (the exact person, even on a shared name), then the name.
  const byId = row.employee_id != null && empById instanceof Map ? empById.get(String(row.employee_id)) : null
  const known = byId || (ownLower ? empByLower.get(ownLower) : null)
  if (ro) return <span>{(known && known.name) || effectiveName || '—'}</span>

  const hasRoster = roster.length > 0
  const prompt = invalid ? <div style={{ fontSize: 11, color: '#c0392b', marginTop: 2 }}>Pick a name for this slot</div> : null
  const opts = roster.filter(e => {
    const nl = String(e.name).trim().toLowerCase()
    return nl === ownLower || !taken.has(nl)   // exclude anyone already on the report, keep own pick
  })
  const pick = (emp) => setRow(idx, { employee_name: emp.name, employee_id: emp.id, _manual: false, ...pickExtra })

  // Linked to an employee who isn't on THIS viewer's list (a defensive case now
  // that inactive employees are listed — e.g. the roster read fell back to
  // active-only). Keep the link —
  // don't drop the row into the free-text box, which would read "Employee ID
  // will be blank" and clear the id on the first keystroke.
  if (!known && row.employee_id != null && ownLower !== '' && !row._manual) {
    return (
      <div>
        <span>{effectiveName}</span>
        {hasRoster && <EmployeePicker variant="link" label="change" opts={opts} onPick={pick} />}
      </div>
    )
  }

  // Manual when explicitly toggled, when there's no roster to pick from, or when
  // the current name isn't on the roster (e.g. a typed-in new hire).
  const isManual = !!row._manual || !hasRoster || (ownLower !== '' && !known)
  if (isManual) {
    return (
      <div>
        <input type="text" style={invalid ? { ...sx.input, ...REQUIRED_FIELD_STYLE } : sx.input} value={effectiveName} placeholder="New hire name"
          onChange={e => setRow(idx, { employee_name: e.target.value, employee_id: null })} />
        {prompt}
        {hasRoster && <EmployeePicker variant="link" label="choose from employee list" opts={opts} onPick={pick} />}
        {ownLower !== '' && !known && (
          <div style={{ fontSize: 11, color: '#c0392b', marginTop: 2 }}>Not on roster — Employee ID will be blank</div>
        )}
      </div>
    )
  }

  return (
    <div>
      <EmployeePicker variant="field" selected={known} opts={opts} onPick={pick} invalid={invalid}
        onClear={() => setRow(idx, { employee_name: '', employee_id: null })}
        onManual={() => setRow(idx, { _manual: true, employee_id: null })} />
      {prompt}
      {known && known.active === false && <div style={sx.inactiveWarn}>⚠ {INACTIVE_WARN}</div>}
    </div>
  )
}

// Walk-on / off-schedule workers. When editable (`!ro`) the user can append new
// blank rows and remove rows; the whole array is replaced through the existing
// `set` callback (deepSet on a length-1 path), so no new save path is needed.
function UnscheduledTable({ rows, ro, set, costCodes, employees, takenNames, validate, scope }) {
  const list = Array.isArray(rows) ? rows : []
  const showEdit = !ro
  const roster = Array.isArray(employees) ? employees.filter(e => e && e.name) : []
  const empByLower = buildEmpByLower(roster)
  const empById = new Map(roster.map(e => [String(e.id), e]))
  const taken = takenNames instanceof Set ? takenNames : new Set()

  const setRow = (i, patch) => set(['unscheduled_workers', i], { ...list[i], ...patch })
  const addRow = () => set(['unscheduled_workers'], [...list, blankUnscheduledWorker()])
  const removeRow = (i) => set(['unscheduled_workers'], list.filter((_, j) => j !== i))
  return (
    <>
      <ScrollX>
        <table style={sx.table}>
          <thead><tr>
            <th style={sx.th}>Employee</th>
            <th style={{ ...sx.th, textAlign: 'center' }}>Scheduled?</th>
            <th style={{ ...sx.th, textAlign: 'center' }}>Actual Hours</th>
            <th style={sx.th}>Cost Code</th>
            <th style={sx.th}>Notes</th>
            {showEdit && <th style={{ ...sx.th, textAlign: 'center' }}></th>}
          </tr></thead>
          <tbody>
            {list.length === 0 && (
              <tr><td style={{ ...sx.td, color: '#999' }} colSpan={showEdit ? 6 : 5}>No walk-on workers added.</td></tr>
            )}
            {list.map((u, i) => {
              const cells = costCodeCells({
                row: u, ro, costCodes, invalid: validate && needsCostCode(u),
                requiredHours: false, setRow: patch => setRow(i, patch), scope,
              })
              return (
                <tr key={i} style={{ background: i % 2 ? '#f9f9f9' : '#fff' }}>
                  <td style={sx.td}>
                    <WalkOnNameCell ro={ro} row={u} idx={i} setRow={setRow} empByLower={empByLower} empById={empById} roster={roster} taken={taken} />
                    <EmpIdTag row={u} empByLower={empByLower} empById={empById} />
                  </td>
                  <td style={{ ...sx.td, textAlign: 'center', color: '#b45309' }}>No</td>
                  <td style={{ ...sx.td, textAlign: 'center' }}>{cells.hours}</td>
                  <td style={sx.td}>{cells.code}</td>
                  <td style={sx.td}><TextCell ro={ro} value={u.task_note} onChange={v => set(['unscheduled_workers', i, 'task_note'], v)} /></td>
                  {showEdit && (
                    <td style={{ ...sx.td, textAlign: 'center' }}>
                      <button type="button" onClick={() => removeRow(i)} title="Remove this worker"
                        style={{ border: 'none', background: 'none', color: '#c0392b', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </ScrollX>
      <ZeroHoursNote rows={list} ro={ro} />
      {showEdit && (
        <button type="button" onClick={addRow}
          style={{ marginTop: 8, border: '1px solid #cfd6dd', background: '#f7f9fb', color: '#1e2d3d', borderRadius: 4, padding: '6px 12px', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
          + Add worker
        </button>
      )}
    </>
  )
}

function Issues({ form, ro, set }) {
  if (ro) {
    const occurred = ISSUE_TYPES.filter(t => form[t.key]?.occurred)
    const notOccurred = ISSUE_TYPES.filter(t => !form[t.key]?.occurred)
    return (
      <>
        {occurred.map(t => (
          <div key={t.key} style={sx.issueBox}>
            <div style={sx.issueLabel}>⚠ {t.label}</div>
            {form[t.key].description && <p style={{ ...sx.p, margin: '6px 0 0' }}>{form[t.key].description}</p>}
          </div>
        ))}
        {notOccurred.length > 0 && <p style={sx.muted}>No issues reported: {notOccurred.map(t => t.label).join(', ')}</p>}
        {occurred.length === 0 && notOccurred.length === 0 && <p style={sx.muted}>No issues recorded.</p>}
      </>
    )
  }
  return (
    <>
      {ISSUE_TYPES.map(t => {
        const v = form[t.key] || { occurred: false, description: null }
        return (
          <div key={t.key} style={{ border: '1px solid #eee', borderRadius: 6, padding: '10px 12px', margin: '8px 0' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#444', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!v.occurred} onChange={e => set([t.key, 'occurred'], e.target.checked)} />
              {t.label}
            </label>
            {v.occurred && (
              <textarea
                style={{ ...sx.textarea, marginTop: 8 }} rows={2}
                value={v.description || ''}
                onChange={e => set([t.key, 'description'], e.target.value)}
                placeholder={`Describe the ${t.label.toLowerCase()}…`}
              />
            )}
          </div>
        )
      })}
    </>
  )
}

// Unified production table (Cost Code | Quantity | Notes) — one editable list,
// no concrete-vs-masonry choice for the editor. The STORED shape is unchanged
// (the voice extraction still writes production.concrete[] {cost_code,
// cubic_yards, notes} and production.masonry[] {unit_type, quantity}): each
// row edits in place in its home array — concrete maps Quantity↔cubic_yards,
// masonry maps Quantity↔quantity and Notes↔unit_type. Rows added here append
// to concrete[] (the generic {cost_code, cubic_yards, notes} shape). Add /
// remove mirrors UnscheduledTable: the whole array is replaced through the
// existing `set` callback, so no new save path is needed.
const blankProductionRow = () => ({ cost_code: null, cubic_yards: null, notes: null })

function Production({ production, ro, set, costCodes, scope }) {
  const concrete = production?.concrete || []
  const masonry = production?.masonry || []
  const rows = [
    ...concrete.map((c, i) => ({
      key: `c${i}`,
      cost_code: c.cost_code,
      quantity: c.cubic_yards,
      notes: c.notes,
      setCode: v => set(['production', 'concrete', i, 'cost_code'], v),
      setQty: v => set(['production', 'concrete', i, 'cubic_yards'], v),
      setNotes: v => set(['production', 'concrete', i, 'notes'], v),
      remove: () => set(['production', 'concrete'], concrete.filter((_, j) => j !== i)),
    })),
    ...masonry.map((m, i) => ({
      key: `m${i}`,
      cost_code: m.cost_code,
      quantity: m.quantity,
      notes: m.unit_type,
      setCode: v => set(['production', 'masonry', i, 'cost_code'], v),
      setQty: v => set(['production', 'masonry', i, 'quantity'], v),
      setNotes: v => set(['production', 'masonry', i, 'unit_type'], v),
      remove: () => set(['production', 'masonry'], masonry.filter((_, j) => j !== i)),
    })),
  ]
  if (ro && rows.length === 0) {
    return <p style={sx.muted}>No production recorded today.</p>
  }
  const addRow = () => set(['production', 'concrete'], [...concrete, blankProductionRow()])
  return (
    <>
      {rows.length > 0 ? (
        <ScrollX>
          <table style={sx.table}>
            <thead><tr>
              <th style={sx.th}>Cost Code</th>
              <th style={{ ...sx.th, textAlign: 'center' }}>Quantity</th>
              <th style={sx.th}>Notes</th>
              {!ro && <th style={{ ...sx.th, textAlign: 'center' }}></th>}
            </tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.key} style={{ background: i % 2 ? '#f9f9f9' : '#fff' }}>
                  <td style={sx.td}>
                    <CostCodeCell ro={ro} value={r.cost_code} onChange={r.setCode} costCodes={costCodes}
                      scopeItems={scope ? scope.items : null}
                      onPickTask={item => { r.setCode(item.cost_code); r.setNotes(noteAfterPick(r.notes, item, scope ? scope.names : [])) }} />
                  </td>
                  <td style={{ ...sx.td, textAlign: 'center' }}><NumCell ro={ro} value={r.quantity} onChange={r.setQty} /></td>
                  <td style={sx.td}><TextCell ro={ro} value={r.notes} onChange={r.setNotes} placeholder="CY poured, block type/count, etc." /></td>
                  {!ro && (
                    <td style={{ ...sx.td, textAlign: 'center' }}>
                      <button type="button" onClick={r.remove} title="Remove this production entry"
                        style={{ border: 'none', background: 'none', color: '#c0392b', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollX>
      ) : (
        <p style={sx.muted}>No production recorded today.</p>
      )}
      {!ro && (
        <button type="button" onClick={addRow}
          style={{ marginTop: 8, border: '1px solid #cfd6dd', background: '#f7f9fb', color: '#1e2d3d', borderRadius: 4, padding: '6px 12px', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
          + Add production
        </button>
      )}
    </>
  )
}

// Collapsible reference of the job's PlanSwift scope, grouped by cost code
// (quantities + man-days only — job_scope_items carries NO money). Collapsed by
// default; hidden when the job has no scope rows. Hours = man-days x 8.
export function JobScope({ scopeItems, costCodes }) {
  const items = Array.isArray(scopeItems) ? scopeItems : []
  if (!items.length) return null
  const groups = searchScope(items, costCodes, '')
  const totalMd = items.reduce((t, i) => t + (Number(i.man_days) || 0), 0)
  return (
    <details style={{ margin: '18px 0 0', border: '1px solid #e3e7ec', borderRadius: 6, background: '#fbfcfd' }}>
      <summary style={{ cursor: 'pointer', padding: '10px 12px', fontSize: 14, fontWeight: 600, color: '#333' }}>
        Job scope (from estimate)
        <span style={{ fontWeight: 400, fontSize: 12, color: '#888' }}> · {items.length} task{items.length === 1 ? '' : 's'}{totalMd > 0 ? ` · ${fmtManDays(totalMd)}` : ''}</span>
      </summary>
      <div style={{ padding: '0 12px 12px' }}>
        {groups.map(g => {
          const md = g.items.reduce((t, x) => t + (Number(x.item.man_days) || 0), 0)
          return (
            <div key={g.code} style={{ marginTop: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, background: '#1e2d3d', color: '#fff', borderRadius: 4, padding: '5px 10px', fontSize: 13, fontWeight: 700 }}>
                <span>{g.code}{g.description ? ` — ${g.description}` : ''}</span>
                <span style={{ fontWeight: 400, whiteSpace: 'nowrap' }}>{fmtManDays(md)}</span>
              </div>
              <ScrollX>
                <table style={{ ...sx.table, minWidth: 380 }}>
                  <thead><tr>
                    <th style={sx.th}>Task</th>
                    <th style={sx.th}>Size</th>
                    <th style={{ ...sx.th, textAlign: 'right' }}>Qty</th>
                    <th style={{ ...sx.th, textAlign: 'right' }}>Man-days</th>
                  </tr></thead>
                  <tbody>
                    {g.items.map(({ item }, k) => (
                      <tr key={k} style={{ background: k % 2 ? '#f9f9f9' : '#fff' }}>
                        <td style={sx.td}>{item.task_name}{item.area ? <span style={{ marginLeft: 6, fontSize: 11, color: '#4a90d9' }}>[{item.area}]</span> : null}</td>
                        <td style={{ ...sx.td, fontSize: 12, color: '#666' }}>{item.size_text || ''}</td>
                        <td style={{ ...sx.td, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtQty(item.qty)} {fmtQty(item.qty) ? (item.unit || '') : ''}</td>
                        <td style={{ ...sx.td, textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(item.man_days) > 0 ? Number(item.man_days).toFixed(1) : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollX>
            </div>
          )
        })}
      </div>
    </details>
  )
}

// ── composed layout ───────────────────────────────────────────────────────────
export function ReportHeader({ report }) {
  const submittedEarly = isSubmittedEarly(report)
  return (
    <div style={sx.header}>
      <div style={sx.eyebrow}>Daily Field Report</div>
      <div style={sx.title}>{report.job_number} — {report.job_name}</div>
      <div style={sx.hMeta}>
        <div>Work Date: {fmtDate(report.report_date)}</div>
        <div>Foreman: {report.foreman_name || '—'}</div>
        {fmtDuration(report.call_duration_seconds) && <div>Call Duration: {fmtDuration(report.call_duration_seconds)}</div>}
      </div>
      {submittedEarly && (
        <div style={sx.earlyChip} title="The foreman submitted this report before its Work Date — confirm the hours are for the correct day.">
          ⚠ submitted before work date — confirm hours
        </div>
      )}
    </div>
  )
}

// All report sections (weather + the seven editable blocks). `ro` toggles
// read-only vs editable. Banners and the bottom action live in the page.
// `scopeItems` (optional) = the job's PlanSwift task rows from the API
// (scope_items). Present -> editable cost-code cells become the task picker and
// the "Job scope" reference renders; absent/empty -> everything renders as before.
export function ReportBodySections({ report, form, ro, set, costCodes, fillIn, onDeleteAttendance, employees, validate, scopeItems }) {
  // Names already on the report (scheduled + walk-ons) — excluded from the
  // walk-on roster dropdown so nobody can be added twice. Case-insensitive.
  const takenNames = new Set(
    [...(form.attendance || []), ...(form.unscheduled_workers || [])]
      .map(w => w && w.employee_name)
      .filter(Boolean)
      .map(n => String(n).trim().toLowerCase())
  )
  const scopeList = Array.isArray(scopeItems) ? scopeItems.filter(i => i && i.task_name) : []
  const scope = scopeList.length ? { items: scopeList, names: scopeList.map(i => i.task_name) } : null
  return (
    <>
      <WeatherBlock report={report} />

      <h3 style={sx.h3}>Work Performed</h3>
      {ro
        ? <p style={sx.p}>{form.work_description || <em style={{ color: '#999' }}>None recorded</em>}</p>
        : <textarea style={sx.textarea} rows={4} value={form.work_description || ''}
            onChange={e => set(['work_description'], e.target.value)} placeholder="What the crew physically did today…" />}

      <JobScope scopeItems={scopeList} costCodes={costCodes} />

      <h3 style={sx.h3}>Manpower</h3>
      <ManpowerTable rows={form.attendance} ro={ro} set={set} costCodes={costCodes} fillIn={fillIn} onDeleteRow={onDeleteAttendance} employees={employees} takenNames={takenNames} validate={validate} scope={scope} />

      {(!ro || (form.unscheduled_workers || []).length > 0) && (
        <>
          <h3 style={sx.h3}>Unscheduled Workers</h3>
          <UnscheduledTable rows={form.unscheduled_workers || []} ro={ro} set={set} costCodes={costCodes} employees={employees} takenNames={takenNames} validate={validate} scope={scope} />
        </>
      )}

      <h3 style={sx.h3}>Site Conditions & Issues</h3>
      <Issues form={form} ro={ro} set={set} />

      <h3 style={sx.h3}>Production</h3>
      <Production production={form.production} ro={ro} set={set} costCodes={costCodes} scope={scope} />

      <h3 style={sx.h3}>Tomorrow’s Needs</h3>
      {ro
        ? <p style={sx.p}>{form.tomorrow_needs || <em style={{ color: '#999' }}>None recorded</em>}</p>
        : <textarea style={sx.textarea} rows={3} value={form.tomorrow_needs || ''}
            onChange={e => set(['tomorrow_needs'], e.target.value)} placeholder="Materials, equipment, or crew needed tomorrow…" />}
    </>
  )
}

export const BRAND_LINE = 'Cornerstone Contracting · Crew Dispatch Daily Report'

// ── UNLINKED-WORKER HARD STOP (2026-10-07) ────────────────────────────────────
// Every approval path answers 400 'unlinked_workers' when a worker with hours
// doesn't resolve to a payroll employee number (see api/lib/unlinked-gate.js).
// The response carries `unlinked` [{ list, index, name, hours, suggestions }],
// the server's (auto-linked) `report_data`, and the whole `roster` (inactive
// included). These helpers turn that into a "Link to employee" control. There
// is NO free-text employee number anywhere — a link is always a roster pick.

// The worker-row patch for a link: the same shape the roster dropdown writes
// (WalkOnNameCell) — exact roster name + id, out of free-text mode. Returns the
// next form, or null when the row at that index is no longer the worker the
// server named (the form changed under us) — the caller must not guess then.
export function linkWorkerInForm(form, entry, emp) {
  const rows = form && Array.isArray(form[entry.list]) ? form[entry.list] : null
  const row = rows ? rows[entry.index] : null
  if (!row || !emp) return null
  const key = (n) => String(n == null ? '' : n).trim().toLowerCase()
  if (key(row.employee_name) !== key(entry.name)) return null
  return deepSet(form, [entry.list, entry.index], { ...row, employee_name: emp.name, employee_id: emp.id, _manual: false })
}

const NOT_IN_FOUNDATION_MSG = 'Not in Foundation yet — ask Carla to add them, then run the Foundation sync (Admin tab) and link here.'

function empLabel(e) {
  return `${e.name} #${e.id}${e.active === false ? ' (inactive)' : ''}`
}

function UnlinkedRow({ entry, roster, onLink, busy }) {
  const [q, setQ] = React.useState('')
  const fq = foldText(q)
  const matches = fq.length >= 2
    ? (roster || []).filter(e => foldText(`${e.name} ${e.id}`).includes(fq)).slice(0, 8)
    : []
  const chip = {
    display: 'inline-block', margin: '4px 6px 0 0', padding: '6px 10px', fontSize: 13, fontWeight: 600,
    border: '1px solid #1e7e34', borderRadius: 16, background: '#fff', color: '#1e7e34',
    cursor: busy ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
  }
  const where = entry.list === 'unscheduled_workers' ? 'Unscheduled workers' : 'Manpower'
  return (
    <div style={{ background: '#fff', border: '1px solid #f1c4c2', borderRadius: 6, padding: '10px 12px', margin: '8px 0 0' }}>
      <div style={{ fontSize: 13, color: '#1f2430' }}>
        <strong>{entry.name}</strong> <span style={{ color: '#888' }}>· {entry.hours} h · {where}</span>
      </div>
      {(entry.suggestions || []).length > 0 && (
        <div style={{ marginTop: 4 }}>
          <span style={{ fontSize: 12, color: '#555', marginRight: 4 }}>Link to employee:</span>
          {entry.suggestions.map(s => (
            <button key={s.id} type="button" style={chip} disabled={busy} onClick={() => onLink(entry, s)}>
              {empLabel(s)}
            </button>
          ))}
        </div>
      )}
      <input type="text" style={{ ...sx.input, marginTop: 8 }} value={q} disabled={busy}
        placeholder="Search all employees by name or #…" onChange={e => setQ(e.target.value)} />
      {matches.length > 0 && (
        <div style={{ border: '1px solid #e2e6ea', borderRadius: 4, marginTop: 4, maxHeight: 220, overflowY: 'auto' }}>
          {matches.map(e => (
            <button key={e.id} type="button" disabled={busy} onClick={() => { setQ(''); onLink(entry, e) }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 10px', fontSize: 13, border: 'none', borderBottom: '1px solid #f0f2f4', background: '#fff', cursor: busy ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
              {empLabel(e)}
            </button>
          ))}
        </div>
      )}
      {fq.length >= 2 && matches.length === 0 && (
        <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>No employee matches “{q}”.</div>
      )}
      <div style={{ fontSize: 12, color: '#8a5a1a', marginTop: 6, lineHeight: 1.5 }}>{NOT_IN_FOUNDATION_MSG}</div>
    </div>
  )
}

// Names the CLIENT can already tell aren't linked, for the pre-submit hint on a
// submit-and-approve button. ADVISORY ONLY — the server decides (it auto-links
// exact unique names, and knows inactive employees the page's active-only
// roster doesn't). Same rows the server checks: a real name, on site, hours > 0
// (splits honoured), and displayEmployeeId() flagging it "not in payroll yet".
export function likelyUnlinkedNames(form, employees) {
  const roster = Array.isArray(employees) ? employees.filter(e => e && e.name) : []
  const empByLower = buildEmpByLower(roster)
  const out = []
  const seen = new Set()
  for (const list of ['attendance', 'unscheduled_workers']) {
    for (const row of (form && Array.isArray(form[list]) ? form[list] : [])) {
      if (!row || row.showed_up === false) continue
      const splits = activeSplits(row)
      const hours = splits ? splitHoursTotal(splits) : Number(row.hours_worked)
      if (!(Number(hours) > 0)) continue
      if (!displayEmployeeId(row, empByLower).flagged) continue
      const name = String(row.employee_name).trim()
      const k = name.toLowerCase()
      if (seen.has(k)) continue
      seen.add(k)
      out.push(name)
    }
  }
  return out
}

function joinNames(names) {
  if (names.length <= 1) return names.join('')
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

// The unlinked-worker block. Two faces:
//   default — RED "Can't approve": an approve-only button was blocked.
//   held    — AMBER "Submitted - NOT approved yet": a submit-and-approve button
//             recorded the submission and HELD the approval.
// `note` is an optional line under the list (e.g. "tap Approve Report again").
export function UnlinkedWorkersPanel({ unlinked, roster, onLink, busy, note, held }) {
  const list = Array.isArray(unlinked) ? unlinked : []
  if (!list.length && !note) return null
  // Red (blocked) / amber (held) while anything is still unlinked; green once
  // the last one is linked.
  const colors = !list.length ? ['#eaf6ec', '#1e7e34', '#1e5128']
    : held ? ['#fff8f0', '#e8820c', '#92510a'] : ['#fdecec', '#d9534f', '#a12622']
  const names = joinNames(list.map(e => e.name))
  return (
    <div style={{ ...sx.banner(...colors), marginTop: 0 }}>
      {list.length > 0 && (
        <>
          {held ? (
            <>
              <strong>Submitted - NOT approved yet.</strong> {names} {list.length === 1 ? 'isn’t' : 'aren’t'} linked to a payroll employee number, so these hours can’t go to payroll. Carla has been emailed. Link them below (or once Carla adds them in Foundation) and approve.
            </>
          ) : (
            <>
              <strong>Can’t approve: {list.length} worker{list.length === 1 ? '' : 's'} {list.length === 1 ? 'isn’t' : 'aren’t'} linked to a payroll employee number.</strong>
              <div style={{ fontSize: 12, marginTop: 2 }}>Their hours can’t be paid until each one is linked to a real employee.</div>
            </>
          )}
          {list.map(e => (
            <UnlinkedRow key={`${e.list}:${e.index}:${e.name}`} entry={e} roster={roster} onLink={onLink} busy={busy} />
          ))}
        </>
      )}
      {note && <div style={{ marginTop: list.length ? 10 : 0, fontWeight: 600 }}>{note}</div>}
    </div>
  )
}
